import { useState, useRef } from 'react';
import { Upload, X, Loader2, CheckCircle, AlertTriangle, Download } from 'lucide-react';
import { NAVY, AMBER, BORDER_COLOR, navyBtn, bubbleStyle } from '../../styles/tokens';
import client from '../../api/client';

/**
 * Shared bulk CSV upload modal.
 *
 * Props:
 * - open: boolean
 * - onClose: () => void
 * - onSuccess: () => void  — called after successful upload to refresh parent data
 * - title: string          — e.g. "Carga Masiva de Tareas"
 * - endpoint: string       — e.g. "/api/admin/cost-codes/bulk"
 * - templateColumns: string[] — column headers for the CSV template download
 * - templateExample: string[][] — example rows for the template
 */
export default function BulkUploadModal({ open, onClose, onSuccess, title, endpoint, templateColumns, templateExample }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);

  if (!open) return null;

  function handleFile(f) {
    if (!f) return;
    setFile(f);
    setResult(null);
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      const lines = text.replace(/\r\n/g, '\n').split('\n').filter(l => l.trim());
      const headers = lines[0]?.split(',').map(h => h.trim()) || [];
      const rows = lines.slice(1, 11).map(l => l.split(',').map(v => v.trim()));
      setPreview({ headers, rows, totalRows: lines.length - 1 });
    };
    reader.readAsText(f);
  }

  async function handleUpload() {
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await client.post(endpoint, form);
      setResult(res.data);
      if (res.data.ok && onSuccess) onSuccess();
    } catch (err) {
      setResult({ ok: false, error: err.response?.data?.error || 'Error al subir archivo' });
    } finally {
      setUploading(false);
    }
  }

  function handleDownloadTemplate() {
    const header = templateColumns.join(',');
    const rows = (templateExample || []).map(r => r.join(','));
    const csv = [header, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `plantilla_${title.toLowerCase().replace(/\s+/g, '_')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  function reset() {
    setFile(null);
    setPreview(null);
    setResult(null);
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1000,
      background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center',
      animation: 'fadeIn 0.15s ease',
    }}>
      <div style={{
        background: '#fff', borderRadius: 12, width: '90%', maxWidth: 700,
        maxHeight: '80vh', overflow: 'auto', padding: 28,
        boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
      }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h3 style={{ fontSize: 18, fontWeight: 700, color: NAVY, margin: 0 }}>{title}</h3>
          <button onClick={() => { reset(); onClose(); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#888' }}>
            <X size={20} />
          </button>
        </div>

        {/* Template download */}
        <button
          onClick={handleDownloadTemplate}
          style={{
            display: 'flex', alignItems: 'center', gap: 6, background: 'none',
            border: `1px solid ${BORDER_COLOR}`, borderRadius: 6, padding: '6px 14px',
            fontSize: 13, color: NAVY, cursor: 'pointer', marginBottom: 16,
          }}
        >
          <Download size={14} /> Descargar Plantilla CSV
        </button>

        {/* File drop zone */}
        {!result && (
          <div
            onClick={() => inputRef.current?.click()}
            onDragOver={e => { e.preventDefault(); e.currentTarget.style.borderColor = AMBER; }}
            onDragLeave={e => { e.currentTarget.style.borderColor = BORDER_COLOR; }}
            onDrop={e => { e.preventDefault(); e.currentTarget.style.borderColor = BORDER_COLOR; handleFile(e.dataTransfer.files[0]); }}
            style={{
              border: `2px dashed ${BORDER_COLOR}`, borderRadius: 10, padding: 30,
              textAlign: 'center', cursor: 'pointer', marginBottom: 16,
              background: '#fafafa', transition: 'border-color 0.2s',
            }}
          >
            <Upload size={28} color="#aaa" style={{ marginBottom: 8 }} />
            <div style={{ fontSize: 14, color: '#666' }}>
              {file ? file.name : 'Arrastra un archivo CSV o haz clic para seleccionar'}
            </div>
            <input
              ref={inputRef}
              type="file"
              accept=".csv"
              style={{ display: 'none' }}
              onChange={e => handleFile(e.target.files[0])}
            />
          </div>
        )}

        {/* Preview table */}
        {preview && !result && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 13, color: '#666', marginBottom: 8 }}>
              Vista previa ({preview.totalRows} filas{preview.totalRows > 10 ? ', mostrando primeras 10' : ''})
            </div>
            <div style={{ overflow: 'auto', maxHeight: 250 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr>
                    {preview.headers.map((h, i) => (
                      <th key={i} style={{ padding: '6px 10px', background: '#f1f5f9', borderBottom: `1px solid ${BORDER_COLOR}`, textAlign: 'left', fontWeight: 600, color: NAVY }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {preview.rows.map((row, i) => (
                    <tr key={i}>
                      {row.map((cell, j) => (
                        <td key={j} style={{ padding: '5px 10px', borderBottom: `1px solid ${BORDER_COLOR}`, color: '#444' }}>{cell}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Upload button */}
        {preview && !result && (
          <button
            onClick={handleUpload}
            disabled={uploading}
            style={{ ...navyBtn, display: 'flex', alignItems: 'center', gap: 8, opacity: uploading ? 0.7 : 1 }}
          >
            {uploading ? <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> : <Upload size={16} />}
            {uploading ? 'Subiendo...' : `Subir ${preview.totalRows} registros`}
          </button>
        )}

        {/* Result */}
        {result && (
          <div style={{
            ...bubbleStyle, padding: 20,
            border: `1px solid ${result.ok ? '#86efac' : '#fca5a5'}`,
            background: result.ok ? '#f0fdf4' : '#fef2f2',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
              {result.ok
                ? <CheckCircle size={20} color="#16a34a" />
                : <AlertTriangle size={20} color="#dc2626" />}
              <span style={{ fontWeight: 600, fontSize: 15, color: result.ok ? '#166534' : '#991b1b' }}>
                {result.ok ? 'Carga completada' : 'Error en la carga'}
              </span>
            </div>
            {result.ok ? (
              <div style={{ fontSize: 13, color: '#444' }}>
                <div>Total procesados: <strong>{result.total}</strong></div>
                <div>Nuevos: <strong>{result.inserted}</strong></div>
                <div>Actualizados: <strong>{result.updated}</strong></div>
                {result.errors?.length > 0 && (
                  <div style={{ marginTop: 8, color: '#b91c1c' }}>
                    Errores ({result.errors.length}):
                    <ul style={{ margin: '4px 0 0 16px', padding: 0 }}>
                      {result.errors.map((e, i) => (
                        <li key={i}>Línea {e.line}: {e.error}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ) : (
              <div style={{ fontSize: 13, color: '#991b1b' }}>{result.error}</div>
            )}
            <button
              onClick={reset}
              style={{ marginTop: 14, ...navyBtn, padding: '8px 16px', fontSize: 13 }}
            >
              Subir otro archivo
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
